/**
 * `useVerticalOverflowShadow` — cues "more rows below" by tracking whether the scroll
 * container's body overflows vertically AND is not scrolled to the bottom, so
 * `BottomOverflowShadow` can fade a drop shadow in over the last visible rows (MUI DataGrid
 * `data-with-bottom-overlay` parity). Inert without vertical overflow, so it costs nothing
 * on tables that fit. The vertical twin of `useHorizontalOverflowShadows`.
 *
 * BOTTOM-ONLY by design: `TableHeader` is an in-flow STICKY row at the top of this same
 * scroll container, so the top edge already reads as bounded by the header — which is why
 * the MUI original overlays only the bottom.
 *
 * The shadow is rendered as an OVERLAY on the non-scrolling card, NEVER as a CSS
 * `mask`/`mask-image` on the scroll container: a mask hides the container's scrollbars in
 * Chromium (the bug that dropped the earlier mask-based fade — see `dataTableRootVariants`
 * and commit f7fe90e54c). This hook only measures; the overlay is `BottomOverflowShadow`.
 *
 * Measures on scroll/resize + revalidate-on-content-change, reading the live scroll geometry
 * (`scrollTop`/`scrollHeight`/`clientHeight`) that exists only after layout and changes on
 * scroll/resize events, never on render — so it is effect-driven, not render-derived
 * (measuring in render would force a reflow and read stale/zero geometry under SSR).
 */
import { type RefObject, useEffect, useState } from 'react'
import { useEvent } from 'react-use-event-hook'

export type VerticalOverflowShadow = {
  /** Content is hidden past the bottom edge (overflows AND not scrolled to bottom) → show it. */
  show: boolean
  /**
   * Live height (px) of the container's horizontal scrollbar (`offsetHeight − clientHeight`).
   * The shadow band sits this far ABOVE the card's bottom edge so it never paints over the
   * horizontal scrollbar. `0` with overlay scrollbars (macOS) or no horizontal overflow.
   * Assumes the scroll element has no top/bottom border (true for `TableRoot`), so
   * `offsetHeight − clientHeight` is the scrollbar alone.
   */
  scrollbarHeight: number
}

const NO_OVERFLOW: VerticalOverflowShadow = { show: false, scrollbarHeight: 0 }

/**
 * @param scrollRef the scroll container (`TableRoot`) ref — stable across renders.
 * @param revalidateKey re-measures when the CONTENT height could change without a viewport
 *   resize (row count, density, populated↔overlay) — the vertical gap a `ResizeObserver` on
 *   a fixed-height container misses.
 * @returns whether the bottom edge hides content (`show`) and the horizontal-scrollbar
 *   height to clear; `{ show: false, scrollbarHeight: 0 }` when the content fits or the
 *   container isn't mounted yet.
 */
export const useVerticalOverflowShadow = (
  scrollRef: RefObject<HTMLElement | null>,
  revalidateKey: unknown,
): VerticalOverflowShadow => {
  const [shadow, setShadow] = useState<VerticalOverflowShadow>(NO_OVERFLOW)

  // Stable measurer (never a re-subscribe trigger): reads the live geometry off the ref and
  // updates only when a value flips, so scrolling within one shadow state costs one layout
  // read and no render (React bails on the unchanged object identity returned below).
  const measure = useEvent(() => {
    const element = scrollRef.current
    if (element === null) {
      return
    }
    const { scrollTop, scrollHeight, clientHeight, offsetHeight } = element
    // 1px tolerance absorbs sub-pixel scroll offsets (mirrors `useHorizontalOverflowShadows`).
    // `true` iff content overflows AND is not scrolled to the bottom: a table that fits has
    // `scrollHeight − clientHeight ≈ 0` and `scrollTop = 0`, so the gap stays ≤ 1.
    const show = scrollHeight - clientHeight - scrollTop > 1
    // Horizontal scrollbar height: `offsetHeight` includes it, `clientHeight` does not (and
    // neither includes a border, which `TableRoot` has none of). The band sits above it.
    const scrollbarHeight = offsetHeight - clientHeight
    setShadow((prev) =>
      prev.show === show && prev.scrollbarHeight === scrollbarHeight
        ? prev
        : { show, scrollbarHeight },
    )
  })

  // Subscribe ONCE (scroll + viewport resize). Keyed only on the stable `scrollRef`/`measure`
  // so a data change never tears the listeners down and rebuilds them.
  useEffect(
    function subscribeToScrollAndResize() {
      const element = scrollRef.current
      if (element === null) {
        return
      }

      measure()
      element.addEventListener('scroll', measure, { passive: true })
      const resizeObserver = new ResizeObserver(measure)
      resizeObserver.observe(element)

      return () => {
        element.removeEventListener('scroll', measure)
        resizeObserver.disconnect()
      }
    },
    [scrollRef, measure],
  )

  // Re-measure when the content height could change without a scroll or viewport resize (rows
  // loaded/removed, density change, populated↔overlay) — the gap a `ResizeObserver` on a
  // fixed-height container misses. No re-subscribe: only a re-read.
  useEffect(
    function remeasureOnContentChange() {
      measure()
    },
    [measure, revalidateKey],
  )

  return shadow
}
